<!--
  Installed from https://vue-bits.dev/ui/
-->
<template>
  <div
    ref="textRef"
    :class="`split-parent whitespace-normal ${className}`"
    :style="{
      textAlign,
      wordWrap: 'break-word'
    }"
  >
    {{ text }}
  </div>
</template>

<script>
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { SplitText as GSAPSplitText } from 'gsap/SplitText';

gsap.registerPlugin(ScrollTrigger, GSAPSplitText);

export default {
  name: 'SplitText',
  props: {
    text: {
      type: String,
      required: true
    },
    className: {
      type: String,
      default: ''
    },
    delay: {
      type: Number,
      default: 100
    },
    duration: {
      type: Number,
      default: 0.6
    },
    ease: {
      type: [String, Function],
      default: 'power3.out'
    },
    splitType: {
      type: String,
      default: 'chars',
      validator: val => ['chars', 'words', 'lines', 'words, chars'].includes(val)
    },
    from: {
      type: Object,
      default: () => ({ opacity: 0, y: 40 })
    },
    to: {
      type: Object,
      default: () => ({ opacity: 1, y: 0 })
    },
    threshold: {
      type: Number,
      default: 0.1
    },
    rootMargin: {
      type: String,
      default: '-100px'
    },
    textAlign: {
      type: String,
      default: 'center',
      validator: val => ['left', 'center', 'right', 'justify'].includes(val)
    },
    onLetterAnimationComplete: {
      type: Function,
      default: null
    }
  },
  emits: ['animation-complete'],
  data() {
    return {
      animationCompleted: false,
      scrollTrigger: null,
      timeline: null,
      splitter: null
    };
  },
  computed: {
    textRef() {
      return this.$refs.textRef;
    }
  },
  methods: {
    async initializeAnimation() {
      if (typeof window === 'undefined' || !this.textRef || !this.text) return;

      await this.$nextTick();
      if (document.fonts && document.fonts.ready) {
        try {
          await document.fonts.ready;
        } catch (e) { /* ignore */ }
      }
      if (this.textRef.textContent.trim() !== this.text.trim()) {
        this.textRef.textContent = this.text;
      }
      const el = this.textRef;
      this.animationCompleted = false;
      const absoluteLines = this.splitType === 'lines';
      if (absoluteLines) {
        el.style.position = 'relative';
      }

      let splitter;
      try {
        splitter = new GSAPSplitText(el, {
          type: this.splitType,
          absolute: absoluteLines,
          linesClass: 'split-line',
          autoSplit: true
        });
        this.splitter = splitter;
      } catch (error) {
        return;
      }

      let targets;
      switch (this.splitType) {
        case 'lines':
          targets = splitter.lines;
          break;
        case 'words':
          targets = splitter.words;
          break;
        case 'chars':
          targets = splitter.chars;
          break;
        default:
          targets = splitter.chars;
      }

      if (!targets || targets.length === 0) {
        splitter.revert();
        return;
      }

      targets.forEach(t => {
        t.style.willChange = 'transform, opacity';
      });

      const startPct = (1 - this.threshold) * 100;
      const marginMatch = /^(-?\d+(?:\.\d+)?)(px|em|rem|%)?$/.exec(this.rootMargin);
      const marginValue = marginMatch ? parseFloat(marginMatch[1]) : 0;
      const marginUnit = marginMatch ? marginMatch[2] || 'px' : 'px';
      const sign = marginValue < 0 ? `-=${Math.abs(marginValue)}${marginUnit}` : `+=${marginValue}${marginUnit}`;
      const start = `top ${startPct}%${sign}`;
      const scroller = document.querySelector('.layout-content') || undefined;

      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: el,
          scroller,
          start,
          toggleActions: 'play none none none',
          once: true,
          onToggle: (self) => {
            this.scrollTrigger = self;
          }
        },
        smoothChildTiming: true,
        onComplete: () => {
          this.animationCompleted = true;
          gsap.set(targets, {
            ...this.to,
            clearProps: 'willChange',
            immediateRender: true
          });
          if (this.onLetterAnimationComplete) {
            this.onLetterAnimationComplete();
          }
          this.$emit('animation-complete');
        }
      });

      this.timeline = tl;

      tl.set(targets, { ...this.from, immediateRender: false, force3D: true });
      tl.to(targets, {
        ...this.to,
        duration: this.duration,
        ease: this.ease,
        stagger: this.delay / 1000,
        force3D: true
      });
    },
    cleanup() {
      if (this.timeline) {
        this.timeline.kill();
        this.timeline = null;
      }
      if (this.scrollTrigger) {
        this.scrollTrigger.kill();
        this.scrollTrigger = null;
      }
      if (this.splitter) {
        gsap.killTweensOf(this.textRef);
        this.splitter.revert();
        this.splitter = null;
      }
    }
  },
  mounted() {
      this.initializeAnimation();
  },
  beforeDestroy() {
      this.cleanup();
  },
  watch: {
    text() {
      this.cleanup();
      this.initializeAnimation();
    },
    delay() {
      this.cleanup();
      this.initializeAnimation();
    },
    duration() {
      this.cleanup();
      this.initializeAnimation();
    },
    ease() {
      this.cleanup();
      this.initializeAnimation();
    },
    splitType() {
      this.cleanup();
      this.initializeAnimation();
    },
    from() {
      this.cleanup();
      this.initializeAnimation();
    },
    to() {
      this.cleanup();
      this.initializeAnimation();
    },
    threshold() {
      this.cleanup();
      this.initializeAnimation();
    },
    rootMargin() {
      this.cleanup();
      this.initializeAnimation();
    },
    onLetterAnimationComplete() {
      this.cleanup();
      this.initializeAnimation();
    }
  }
};
</script>